import React from "react";
import { createTheme } from "@mui/material/styles";
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from "react-router-dom";

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }
>((props, ref) => {
  const { href, ...other } = props;
  return <RouterLink ref={ref} to={href} {...other} />;
});

export const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "rgb(31, 93, 107)",
      light: "rgba(31, 93, 107, 0.7)",
      dark: "rgb(20, 60, 70)",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "rgba(55, 191, 190, 0.9)",
      light: "rgba(55, 191, 190, 0.6)",
      dark: "rgba(55, 191, 190, 1)",
      contrastText: "#ffffff",
    },
    background: {
      paper: "#ffffff",
      default: "#F6F7F9",
    },
    action: {
      hover: "rgba(31, 93, 107, 0.08)",
      selected: "rgba(31, 93, 107, 0.12)",
      disabled: "rgba(0, 0, 0, 0.38)",
      disabledBackground: "rgba(0, 0, 0, 0.10)",
      focus: "rgba(31, 93, 107, 0.18)",
    },
    divider: "rgba(0,0,0,0.10)",
    text: {
      primary: "rgba(0,0,0,0.88)",
      secondary: "rgba(0,0,0,0.62)",
    },
  },

  shape: {
    borderRadius: 12,
  },

  typography: {
    fontFamily: [
      "Lato",
      "Inter",
      "system-ui",
      "-apple-system",
      "Segoe UI",
      "Roboto",
      "Helvetica",
      "Arial",
      "sans-serif",
    ].join(","),
    h4: { fontSize: "2rem", fontWeight: 800, letterSpacing: -0.4 },
    h5: { fontSize: "1.5rem", fontWeight: 800, letterSpacing: -0.2 },
    h6: { fontSize: "1.125rem", fontWeight: 800 },
    body1: { fontSize: "1rem", lineHeight: 1.55 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    button: { fontWeight: 700, letterSpacing: 0 },
  },

  shadows: [
    "none",
    "0px 1px 2px rgba(0,0,0,0.06)",
    "0px 2px 8px rgba(0,0,0,0.08)",
    "0px 6px 18px rgba(0,0,0,0.10)",
    "0px 10px 30px rgba(0,0,0,0.12)",
    "0px 14px 40px rgba(0,0,0,0.14)",
    "0px 18px 54px rgba(0,0,0,0.16)",
    "0px 22px 70px rgba(0,0,0,0.18)",
    "0px 26px 86px rgba(0,0,0,0.20)",
    "0px 30px 100px rgba(0,0,0,0.22)",
    ...Array.from({ length: 15 }, () => "0px 30px 100px rgba(0,0,0,0.22)"),
  ] as any,

  components: {
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },

    MuiLink: {
      defaultProps: {
        underline: "hover",
        component: LinkBehavior,
      },
      styleOverrides: {
        root: {
          fontWeight: 700,
          textUnderlineOffset: "3px",
        },
      },
    },

    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: "transparent",
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          color: "inherit",
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 80,
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: 16,
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0px 12px 34px rgba(0,0,0,0.06)",
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 999,
          paddingInline: 16,
          paddingBlock: 10,
        },
        containedPrimary: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 10px 26px rgba(31,93,107,0.22)",
          },
        },
        outlined: {
          borderWidth: 1,
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "medium",
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: "rgba(255,255,255,0.9)",
          transition: "box-shadow 140ms ease, border-color 140ms ease",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(31,93,107,0.35)",
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 4px rgba(31,93,107,0.12)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(31,93,107,0.55)",
            borderWidth: 1,
          },
        },
        input: {
          paddingTop: 12,
          paddingBottom: 12,
        },
        notchedOutline: {
          borderColor: "rgba(0,0,0,0.12)",
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: "1px solid rgba(0,0,0,0.10)",
          boxShadow: "0px 18px 60px rgba(0,0,0,0.16)",
          backgroundColor: "rgba(255,255,255,0.68)",
          backgroundImage: "none",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",

          overflow: "hidden",
        },
        list: {
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          marginLeft: 4,
          marginRight: 4,
          marginTop: 2,
          marginBottom: 2,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(0,0,0,0.10)",
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
        },
      },
    },
  },
});
